package br.com.vvrefeicoes.portal.domain.model

/** Unidade cadastrada, com a diretoria e a gestora do organograma publicado. */
data class RhUnitOption(
    val id: String,
    val name: String,
    val director: String?,
    val manager: String?,
)

/** Como o backend chegou à unidade sugerida para um departamento da planilha. */
enum class RhUnitMatchStatus {
    /** Departamento já conferido em uma importação anterior. */
    CONFIRMED,

    /** A unidade tem o mesmo nome do departamento. */
    EXACT,

    /** Sugestão por semelhança de nome: precisa ser conferida. */
    SUGGESTED,
    NOT_FOUND,
}

data class RhDepartmentSuggestion(
    val department: String,
    val suggestedUnitId: String?,
    val status: RhUnitMatchStatus,
)

data class RhUnitPreview(
    val units: List<RhUnitOption>,
    val departments: List<RhDepartmentSuggestion>,
)

/** Linha da conferência de unidades: o departamento da planilha e a unidade escolhida para ele. */
data class RhDepartmentUnit(
    val department: String,
    val employees: Int,
    val status: RhUnitMatchStatus,
    val unitId: String?,
)

/**
 * Monta a conferência a partir dos departamentos da planilha e das sugestões do backend. Os
 * departamentos sem unidade vêm primeiro e as sugestões por semelhança logo depois, para o usuário
 * conferir antes o que mais precisa de atenção.
 */
fun departmentUnits(
    departments: List<String>,
    preview: RhUnitPreview,
): List<RhDepartmentUnit> {
    val counts = departments.groupingBy { it }.eachCount()
    val suggestions = preview.departments.associateBy { it.department }
    val unitIds = preview.units.map { it.id }.toSet()
    return counts
        .map { (department, employees) ->
            val suggestion = suggestions[department]
            val unitId = suggestion?.suggestedUnitId?.takeIf { it in unitIds }
            RhDepartmentUnit(
                department = department,
                employees = employees,
                status = if (unitId == null) RhUnitMatchStatus.NOT_FOUND else suggestion.status,
                unitId = unitId,
            )
        }.sortedWith(compareBy({ it.status.reviewOrder() }, { it.department.lowercase() }))
}

private fun RhUnitMatchStatus.reviewOrder(): Int =
    when (this) {
        RhUnitMatchStatus.NOT_FOUND -> 0
        RhUnitMatchStatus.SUGGESTED -> 1
        RhUnitMatchStatus.EXACT -> 2
        RhUnitMatchStatus.CONFIRMED -> 3
    }
